// pages/Community/CommunityContacts.jsx
//
// The "Contacts" tab (Phase 3). Per-brand audience directory: inbound-captured
// contacts (implicit opt-in) + CSV import. Opt-in is tracked explicitly — only
// opted-in contacts can be targeted by campaigns (Phase 4), so no cold outreach.

import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import addNotification from "helpers/notify";
import { getContacts, importContacts, updateContact, deleteContact } from "api/messaging/messaging";

// Parse pasted CSV: header row with name/whatsapp/telegram/email/phone columns.
function parseCsv(text) {
  const lines = String(text || "").trim().split(/\r?\n/).filter(Boolean);
  if (!lines.length) return [];
  const head = lines[0].split(",").map((h) => h.trim().toLowerCase());
  const idx = (k) => head.indexOf(k);
  const hasHeader = ["name", "whatsapp", "phone", "telegram", "email"].some((k) => idx(k) >= 0);
  const rows = hasHeader ? lines.slice(1) : lines;
  return rows.map((line) => {
    const cols = line.split(",").map((c) => c.trim());
    if (hasHeader) return { name: cols[idx("name")] || "", whatsapp: cols[idx("whatsapp")] || cols[idx("phone")] || "", telegram: cols[idx("telegram")] || "", email: cols[idx("email")] || "", phone: cols[idx("phone")] || "" };
    return { name: cols[0] || "", whatsapp: cols[1] || "" }; // name,number
  }).filter((r) => r.name || r.whatsapp);
}

const CommunityContacts = ({ brandId: brandIdProp }) => {
  const brandsRedux = useSelector((s) => s.user.brands) || [];
  const [brandId, setBrandId] = useState(brandIdProp || "");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [csv, setCsv] = useState("");
  const [optInImport, setOptInImport] = useState(true);
  const [importing, setImporting] = useState(false);

  const selectedBrand = brandsRedux.find((b) => String(b._id) === String(brandId));
  const brandSettingsId = selectedBrand && selectedBrand.brandSettings && selectedBrand.brandSettings._id;

  const load = useCallback(async () => {
    if (!brandId) { setRows([]); return; }
    setLoading(true);
    try { const res = await getContacts({ brandId }); setRows((res && res.data) || []); }
    catch (e) { addNotification((e && e.message) || "Could not load contacts.", "danger"); }
    finally { setLoading(false); }
  }, [brandId]);
  useEffect(() => { load(); }, [load]);

  const doImport = useCallback(async () => {
    const contacts = parseCsv(csv);
    if (!contacts.length) { addNotification("No rows parsed. Use columns: name, whatsapp, telegram, email.", "danger"); return; }
    setImporting(true);
    try {
      const res = await importContacts({ brandId, brandSettingsId, contacts, optIn: optInImport });
      addNotification(`Imported: ${res.created} new, ${res.updated} updated.`, "success");
      setCsv(""); load();
    } catch (e) { addNotification((e && e.message) || "Import failed.", "danger"); }
    finally { setImporting(false); }
  }, [csv, brandId, brandSettingsId, optInImport, load]);

  const toggleOptIn = async (c) => {
    try { await updateContact(c._id, { optIn: !c.optIn }); setRows((rs) => rs.map((r) => (r._id === c._id ? { ...r, optIn: !c.optIn } : r))); }
    catch (e) { addNotification((e && e.message) || "Could not update.", "danger"); }
  };
  const remove = async (c) => {
    try { await deleteContact(c._id); setRows((rs) => rs.filter((r) => r._id !== c._id)); }
    catch (e) { addNotification((e && e.message) || "Could not delete.", "danger"); }
  };

  return (
    <div style={{ maxWidth: 900 }}>
      <h5 style={{ fontWeight: 700 }}>Contacts</h5>
      <p className="text-muted" style={{ fontSize: "0.85rem" }}>
        Your audience per brand. People who message you are captured automatically (opted-in).
        Import others below — only <strong>opted-in</strong> contacts can be messaged by campaigns.
      </p>

      <div className="mb-3" style={{ maxWidth: 360 }}>
        <label className="mb-1" style={{ fontSize: "0.8rem", fontWeight: 600 }}>Brand</label>
        <select className="form-control form-control-sm" value={brandId} onChange={(e) => setBrandId(e.target.value)}>
          <option value="">Select a brand…</option>
          {brandsRedux.map((b) => <option key={b._id} value={b._id}>{b.name}</option>)}
        </select>
      </div>

      {brandId ? (
        <>
          <div style={{ border: "1px solid #e3e6ec", borderRadius: 10, padding: 14, marginBottom: 16, background: "#f7f9ff" }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Import contacts (CSV)</div>
            <div className="text-muted mb-2" style={{ fontSize: "0.76rem" }}>Header row: <code>name, whatsapp, telegram, email</code> (or just <code>name,number</code> per line).</div>
            <textarea className="form-control form-control-sm" rows={4} placeholder={"name, whatsapp, email\nJane Buyer, 14155551234, jane@acme.com"} value={csv} onChange={(e) => setCsv(e.target.value)} />
            <label style={{ display: "block", fontSize: "0.8rem", margin: "8px 0" }}>
              <input type="checkbox" checked={optInImport} onChange={(e) => setOptInImport(e.target.checked)} style={{ marginRight: 6 }} />
              I confirm these contacts have consented to be messaged (opt-in)
            </label>
            <button className="btn btn-sm btn-primary m-0" style={{ backgroundColor: "#0b3d91", borderColor: "#0b3d91" }} disabled={importing || !csv.trim()} onClick={doImport}>
              {importing ? "Importing…" : "Import"}
            </button>
          </div>

          {loading ? <div className="text-muted">Loading…</div>
            : !rows.length ? <div className="text-muted">No contacts yet.</div>
            : (
              <table className="table table-sm" style={{ fontSize: "0.82rem" }}>
                <thead><tr><th>Name</th><th>WhatsApp</th><th>Telegram</th><th>Source</th><th>Opt-in</th><th></th></tr></thead>
                <tbody>
                  {rows.map((c) => (
                    <tr key={c._id}>
                      <td>{c.name || "—"}</td>
                      <td>{(c.identities && c.identities.whatsapp) || ""}</td>
                      <td>{(c.identities && c.identities.telegram) || ""}</td>
                      <td><span className="text-muted">{c.source}</span></td>
                      <td>
                        <span onClick={() => toggleOptIn(c)} style={{ cursor: "pointer", color: c.optIn ? "#1a7f37" : "#999", fontWeight: 600 }}>
                          {c.optIn ? "✓ opted-in" : "opt-in"}
                        </span>
                      </td>
                      <td><span onClick={() => remove(c)} style={{ cursor: "pointer", color: "#c00" }}>×</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
        </>
      ) : <div className="text-muted">Pick a brand.</div>}
    </div>
  );
};

export default CommunityContacts;
